// WHAT: Detail page for Oura stress data with stress/recovery balance, resilience analysis, and trend visualization
// WHY:  Provides comprehensive stress analysis with recovery metrics and resilience tracking for health optimization
// DOMAIN: Health → Oura → Pages
// DEPENDENCIES: useOptimizedStress, useOptimizedResilience, useOptimizedResilienceHistory, LineChart component
// RISKS:
//   - NOTE: Stress ratio calculation assumes recovery_high > 0 - may divide by zero
//   - NOTE: Resilience level mapping is heuristic - may not match Oura's exact algorithm
//   - NOTE: Chart data processing uses complex level value calculations - may have edge cases
//   - NOTE: Historical data fallback logic may not always provide accurate trends

import type { OuraResilience, OuraStress } from "@/domains/health/oura/data/ouraTypes";

import { type FC, useState, useMemo } from "react";

import { LineChart } from "@/components/charts/primitives/LineChart";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { useOptimizedResilience } from "@/domains/health/oura/hooks/useOptimizedResilience";
import { useOptimizedResilienceHistory } from "@/domains/health/oura/hooks/useOptimizedResilienceHistory";
import { useOptimizedStress } from "@/domains/health/oura/hooks/useOptimizedStress";

type BadgeVariant =
  | "accent"
  | "default"
  | "destructive"
  | "info"
  | "outline"
  | "primary"
  | "success"
  | "warning";

/**
 * WHAT:
 *   Formats duration in seconds to human-readable hours and minutes.
 *
 * WHY:
 *   Provides consistent duration display format across the component.
 *
 * @param {number} [seconds] - Duration in seconds (optional)
 *
 * @returns {string} Formatted string (e.g., "2h 30m") or "N/A" if no duration
 *
 */
const formatDuration = (seconds?: number): string => {
  if (!seconds) {
    return "N/A";
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${String(hours)}h ${String(minutes)}m`;
  }
  return `${String(minutes)}m`;
};

/**
 * WHAT:
 *   Calculates stress ratio rating based on high stress vs. high recovery times.
 *
 * WHY:
 *   Provides visual feedback on stress-recovery balance using color-coded badges.
 *
 * INPUTS:
 *   - stressHigh: number — Duration of high stress in seconds
 *   - recoveryHigh: number — Duration of high recovery in seconds
 *
 * OUTPUT:
 *   Object with variant (badge color) and label (rating text).
 *
 * RISKS:
 *   - NOTE: Division by zero handled - returns destructive variant if recoveryHigh is 0
 *   - NOTE: Ratio thresholds are heuristic - may need medical validation
 *
 * @param stressHigh - Duration of high stress in seconds
 *
 * @param recoveryHigh - Duration of high recovery in seconds
 *
 * @returns Object with variant and label for badge display
 *
 */
const getStressRatioRating = (
  stressHigh: number,
  recoveryHigh: number,
): { label: string; variant: BadgeVariant } => {
  // WARN: Division by zero check - recoveryHigh must be > 0
  if (recoveryHigh === 0) {
    return { label: "No Recovery", variant: "destructive" };
  }

  // NOTE: Ratio calculation - lower is better (more recovery relative to stress)
  const ratio = stressHigh / recoveryHigh;

  // NOTE: Rating thresholds - lower ratio is better
  if (ratio < 2) {
    return { label: "Optimal", variant: "success" };
  }
  if (ratio <= 2.8) {
    return { label: "Good", variant: "info" };
  }
  if (ratio <= 3.4) {
    return { label: "Fair", variant: "warning" };
  }
  // NOTE: ratio > 3.4
  return { label: "Bad", variant: "destructive" };
};

/**
 * WHAT:
 *   Maps resilience level string to badge variant and German label.
 *
 * WHY:
 *   Provides consistent visual representation of resilience levels across the UI.
 *
 * INPUTS:
 *   - level: string — Resilience level (excellent, strong, solid, fair, poor)
 *
 * OUTPUT:
 *   Object with variant (badge color) and label (German text).
 *
 * @param {string} level - Resilience level (excellent, strong, solid, fair, poor)
 *
 * @returns {{label: string, variant: string}} Object with variant and label for badge display
 *
 */
const getResilienceLevelRating = (level: string): { label: string; variant: BadgeVariant } => {
  switch (level) {
    case "excellent": {
      return { label: "Hervorragend", variant: "success" };
    }
    case "strong": {
      return { label: "Stark", variant: "info" };
    }
    case "solid": {
      return { label: "Durchschnittlich", variant: "default" };
    }
    case "fair": {
      return { label: "Ausreichend", variant: "warning" };
    }
    case "poor": {
      return { label: "Gering", variant: "destructive" };
    }
    default: {
      return { label: level, variant: "default" };
    }
  }
};

/**
 * WHAT:
 *   Maps resilience level string to numeric base value for chart visualization.
 *
 * WHY:
 *   Provides anchor points for fine-grained level value calculations.
 *
 * @param level - Resilience level string
 *
 * @returns Numeric base value (1-5)
 *
 */
const getBaseLevel = (level: string): number => {
  switch (level) {
    case "poor": {
      return 1;
    }
    case "fair": {
      return 2;
    }
    case "solid": {
      return 3;
    }
    case "strong": {
      return 4;
    }
    case "excellent": {
      return 5;
    }
    default: {
      return 3; // Default to middle value
    }
  }
};

/**
 * WHAT:
 *   Calculates fine-grained level value based on contributors and level ranges.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting level value calculation logic.
 *
 * @param baseLevel - Base level value (1-5)
 *
 * @param avg - Average of contributors
 *
 * @param levelString - Level string key
 *
 * @returns Fine-grained level value
 *
 */
const calculateLevelValue = (baseLevel: number, avg: number, levelString: string): number => {
  // NOTE: Level ranges calibrated to match real Oura app behavior
  // Target: 57.1 avg contributors → 3.9 levelValue
  const levelRanges = {
    excellent: { max: 100, min: 75 },
    fair: { max: 40, min: 20 },
    poor: { max: 20, min: 0 },
    solid: { max: 58, min: 40 }, // 57.1 should be near top (3.9)
    strong: { max: 75, min: 60 },
  };

  const levelKey = levelString as keyof typeof levelRanges;
  if (levelKey in levelRanges) {
    const range = levelRanges[levelKey];
    // NOTE: Normalize position within the level range (0-1)
    const rangeMin = range.min;
    const rangeMax = range.max;
    let pos = (avg - rangeMin) / (rangeMax - rangeMin);
    // WARN: Clamp to prevent out-of-range values
    pos = Math.min(Math.max(pos, 0), 1); // clamp between 0 and 1

    // NOTE: Enhanced mapping: LevelValue = LevelBase + RelativePos * 0.9
    // This creates more dynamic visualization within each level
    const offset = pos * 0.9; // Back to 0.9 for 3.9 target
    return Math.round((baseLevel + offset) * 100) / 100;
  }
  return baseLevel;
};

/**
 * WHAT:
 *   Gets data source from resilience history or current resilience.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting fallback logic.
 *
 * @param resilienceHistory - Historical resilience data
 *
 * @param resilience - Current resilience data
 *
 * @returns Array of resilience data
 *
 */
const getResilienceDataSource = (
  resilienceHistory: OuraResilience[] | undefined,
  resilience: OuraResilience | undefined,
): OuraResilience[] => {
  // NOTE: Fallback logic - use historical data if available, otherwise current data
  if (Array.isArray(resilienceHistory) && resilienceHistory.length > 0) {
    return resilienceHistory;
  }
  if (resilience) {
    return [resilience];
  }
  return [];
};

/**
 * WHAT:
 *   Sorts resilience data by date and returns last N items.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting sorting logic.
 *
 * @param dataSource - Array of resilience data
 *
 * @param chartRange - Number of days to include
 *
 * @returns Sorted and filtered resilience data
 *
 */
const sortAndFilterResilienceData = (
  dataSource: OuraResilience[],
  chartRange: number,
): OuraResilience[] => {
  // NOTE: Sort by date and take last N days based on chartRange selection
  const sorted = (
    dataSource as OuraResilience[] & {
      toSorted: (compareFn: (a: OuraResilience, b: OuraResilience) => number) => OuraResilience[];
    }
  ).toSorted((a: OuraResilience, b: OuraResilience) => {
    const dayA = a.day ?? "";
    const dayB = b.day ?? "";
    const dateA = new Date(dayA).getTime();
    const dateB = new Date(dayB).getTime();
    return dateA - dateB;
  });
  return sorted.slice(-chartRange);
};

/**
 * WHAT:
 *   Maps resilience item to chart data format.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting mapping logic.
 *
 * @param item - Resilience data item
 *
 * @returns Chart data point
 *
 */
const mapResilienceToChartData = (
  item: OuraResilience,
): {
  avgContributors: number;
  day: string;
  daytimeRecovery: number;
  level: string | undefined;
  levelValue: number;
  sleepRecovery: number;
  stress: number;
} => {
  // NOTE: Base level values (anchor points) - maps level strings to numeric values
  const levelString = item.level ?? "";
  const baseLevel: number = getBaseLevel(levelString);

  // NOTE: Calculate fine-grained position within the level based on contributors
  let levelValue = baseLevel;

  if (item.contributors) {
    const { daytime_recovery, sleep_recovery, stress } = item.contributors;
    // NOTE: Average of three contributors for overall position calculation
    const stressValue = stress ?? 0;
    const sleepRecoveryValue = sleep_recovery ?? 0;
    const daytimeRecoveryValue = daytime_recovery ?? 0;
    const avg = (stressValue + sleepRecoveryValue + daytimeRecoveryValue) / 3;
    levelValue = calculateLevelValue(baseLevel, avg, levelString);
  }

  const stressContributor = item.contributors?.stress ?? 0;
  const sleepRecoveryContributor = item.contributors?.sleep_recovery ?? 0;
  const daytimeRecoveryContributor = item.contributors?.daytime_recovery ?? 0;

  return {
    avgContributors: item.contributors
      ? (stressContributor + sleepRecoveryContributor + daytimeRecoveryContributor) / 3
      : 0,
    day: (() => {
      const dayValue = item.day ?? "";
      return new Date(dayValue).toLocaleDateString("de-DE", {
        day: "numeric",
        month: "short",
      });
    })(),
    daytimeRecovery: daytimeRecoveryContributor,
    level: item.level,
    levelValue: levelValue,
    sleepRecovery: sleepRecoveryContributor,
    stress: stressContributor,
  };
};

/**
 * WHAT:
 *   Formats y-axis tick values for resilience chart.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting tick formatting logic.
 *
 * @param value - Y-axis tick value
 *
 * @returns Formatted label string
 *
 */
const formatYAxisTick = (value: number): string => {
  switch (value) {
    case 0: {
      return "";
    }
    case 1: {
      return "Gering";
    }
    case 2: {
      return "Ausreichend";
    }
    case 3: {
      return "Durchschnittlich";
    }
    case 4: {
      return "Stark";
    }
    case 5: {
      return "Hervorragend";
    }
    default: {
      return "";
    }
  }
};

/**
 * WHAT:
 *   Chart range button component for selecting time range.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting button rendering logic.
 *
 */
interface ChartRangeButtonProperties {
  active: boolean;
  days: number;
  onClick: () => void;
}

const ChartRangeButton: FC<ChartRangeButtonProperties> = ({ active, days, onClick }) => {
  return (
    <button
      className={`px-3 py-1 text-sm rounded ${
        active ? "bg-blue-600 text-white" : "bg-neutral-700 text-neutral-300 hover:bg-neutral-600"
      }`}
      onClick={onClick}
    >
      {days}d
    </button>
  );
};

interface StressInsightsCardProperties {
  ratioRating: { label: string; variant: BadgeVariant };
  stress: OuraStress | undefined;
}

/**
 * WHAT:
 *   Displays stress insights and recommendations based on stress data.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting insights rendering logic.
 *
 * @param props - Component props
 *
 * @param props.ratioRating - Stress ratio rating
 *
 * @param props.stress - Stress data
 *
 * @returns Stress insights card component
 *
 */
const StressInsightsCard: FC<StressInsightsCardProperties> = ({ ratioRating, stress }) => {
  return (
    <Card padding="lg" variant="default">
      <h3 className="text-lg font-semibold text-neutral-200 mb-4">Stress-Insights</h3>
      <div className="space-y-3 text-neutral-300">
        {ratioRating.variant === "success" ? (
          <p>✅ Optimales Stress-Recovery Verhältnis. Du bist gut ausbalanciert.</p>
        ) : null}
        {ratioRating.variant === "info" ? (
          <p>🔵 Gutes Stress-Recovery Verhältnis. Achte weiterhin auf Erholung.</p>
        ) : null}
        {ratioRating.variant === "warning" ? (
          <p>🟡 Fair Stress-Recovery Verhältnis. Mehr Erholungszeit wäre ideal.</p>
        ) : null}
        {ratioRating.variant === "destructive" ? (
          <p>🔴 Schlechtes Stress-Recovery Verhältnis. Fokus auf Stressreduktion nötig.</p>
        ) : null}

        {stress?.stress_high !== undefined && stress.stress_high > 3600 ? (
          <p>⚠️ Über 1 Stunde hoher Stress. Entspannungstechniken empfohlen.</p>
        ) : null}

        {stress?.recovery_high !== undefined && stress.recovery_high < 1800 ? (
          <p>💤 Weniger als 30 Min Erholung. Mehr Ruhezeiten einplanen.</p>
        ) : null}
      </div>
    </Card>
  );
};

/**
 * WHAT:
 *   Component that displays detailed stress analysis including stress vs. recovery times,
 *   stress ratio evaluation, resilience level tracking, and trend visualization.
 *
 * WHY:
 *   Provides comprehensive stress monitoring beyond simple score display, enabling
 *   data-driven health optimization through stress-recovery balance analysis.
 *
 * BEHAVIOR:
 *   - Loads stress, resilience, and resilience history data
 *   - Calculates stress ratio (stress_high / recovery_high)
 *   - Processes chart data with level value calculations
 *   - Displays stress insights and recommendations
 *   - Supports 7/14/30 day chart range selection
 *
 * RISKS:
 *   - NOTE: Stress ratio calculation assumes recovery_high > 0 - may divide by zero
 *   - NOTE: Resilience level mapping is heuristic - may not match Oura's exact algorithm
 *   - NOTE: Chart data processing uses complex level value calculations - may have edge cases
 *   - NOTE: Historical data fallback logic may not always provide accurate trends
 *
 * @returns {JSX.Element | null} Stress detail component with analysis and charts
 *
 */
export const StressDetail: FC = () => {
  const [chartRange, setChartRange] = useState<number>(7); // 7 Tage Standard

  const { error: stressError, loading: stressLoading, stress } = useOptimizedStress();

  const {
    error: resilienceError,
    loading: resilienceLoading,
    resilience,
  } = useOptimizedResilience();

  const {
    error: historyError,
    loading: historyLoading,
    resilienceHistory,
  } = useOptimizedResilienceHistory(chartRange);

  /**
   * WHAT:
   *   Processes resilience data for chart visualization with fine-grained level values.
   *
   * WHY:
   *   Creates smooth chart visualization by calculating precise level values within
   *   each resilience level based on contributor averages.
   *
   * BEHAVIOR:
   *   - Uses historical data if available, falls back to current data
   *   - Sorts by date and takes last N days based on chartRange
   *   - Calculates base level values (1-5) from level strings
   *   - Computes fine-grained position within level using contributor averages
   *   - Maps contributor values to level ranges for visualization
   *
   * RISKS:
   *   - NOTE: Level range calibration is heuristic - may not match Oura's exact algorithm
   *   - NOTE: Contributor average calculation assumes all three contributors exist
   *   - NOTE: Position normalization may produce edge cases at range boundaries
   *   - PERF: Memoized but recalculates on every resilienceHistory/resilience/chartRange change
   */
  const chartData = useMemo((): {
    avgContributors: number;
    day: string;
    daytimeRecovery: number;
    level: string | undefined;
    levelValue: number;
    sleepRecovery: number;
    stress: number;
  }[] => {
    const dataSource = getResilienceDataSource(resilienceHistory, resilience);

    if (dataSource.length === 0) {
      return [];
    }

    const sortedData = sortAndFilterResilienceData(dataSource, chartRange);
    return sortedData.map((item) => mapResilienceToChartData(item));
  }, [resilienceHistory, resilience, chartRange]);

  const loading = stressLoading || resilienceLoading || historyLoading;
  const error = stressError ?? resilienceError ?? historyError;

  if (loading) {
    return null; // OuraApp handles loading states globally
  }

  if (error) {
    return null; // OuraApp handles error states globally
  }

  const ratioRating: { label: string; variant: BadgeVariant } =
    stress?.stress_high !== undefined && stress.recovery_high !== undefined
      ? getStressRatioRating(stress.stress_high, stress.recovery_high)
      : { label: "N/A", variant: "default" };

  // Get current resilience level
  const currentResilience = resilience ?? null;
  const resilienceRating = currentResilience?.level
    ? getResilienceLevelRating(currentResilience.level)
    : { label: "N/A", variant: "default" };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-neutral-100 mb-2">Stress-Analyse</h1>
        <p className="text-neutral-400">Stress vs. Recovery Balance</p>
      </div>

      {/* Day Summary */}
      <Card padding="lg" variant="default">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Tagesübersicht</h3>
        <div className="grid gap-4">
          {stress ? (
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Tag: {stress.day}</span>
              <Badge
                className="font-medium"
                variant={stress.day_summary === "normal" ? "info" : "warning"}
              >
                {stress.day_summary}
              </Badge>
            </div>
          ) : null}
          {currentResilience ? (
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Resilience Level</span>
              <Badge className="font-medium" variant={resilienceRating.variant as BadgeVariant}>
                {resilienceRating.label}
              </Badge>
            </div>
          ) : null}
        </div>
      </Card>

      {/* Stress vs Recovery Times */}
      {stress ? (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Stress & Recovery Zeiten</h3>
          <div className="grid gap-4">
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Hoher Stress</span>
              <Badge className="font-medium" variant="destructive">
                {formatDuration(stress.stress_high)}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Hohe Erholung</span>
              <Badge className="font-medium" variant="success">
                {formatDuration(stress.recovery_high)}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Stress-Ratio</span>
              <Badge className="font-medium" variant={ratioRating.variant}>
                {ratioRating.label}
              </Badge>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Resilience Chart */}
      {chartData.length > 0 ? (
        <Card padding="lg" variant="default">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-200">Resilience Trend</h3>
            <div className="flex gap-2">
              <ChartRangeButton
                active={chartRange === 7}
                days={7}
                onClick={() => {
                  setChartRange(7);
                }}
              />
              <ChartRangeButton
                active={chartRange === 14}
                days={14}
                onClick={() => {
                  setChartRange(14);
                }}
              />
              <ChartRangeButton
                active={chartRange === 30}
                days={30}
                onClick={() => {
                  setChartRange(30);
                }}
              />
            </div>
          </div>

          {/* Line Chart Visualization */}
          <LineChart
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
            color="#ffffff"
            data={chartData}
            gridLines={{
              horizontal: true,
              horizontalColor: "rgba(255, 255, 255, 0.1)",
              horizontalValues: [1, 2, 3, 4],
              vertical: false,
            }}
            height={300}
            xKey="day"
            yAxisDomain={[0, 5]}
            yAxisTickFormatter={formatYAxisTick}
            yAxisTicks={[0, 1, 2, 3, 4, 5]}
            yKey="levelValue"
          />

          {/* Chart Legend */}
          <div className="flex justify-center gap-3 mt-4 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded" />
              <span className="text-neutral-300">Hervorragend</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              <span className="text-neutral-300">Stark</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-500 rounded" />
              <span className="text-neutral-300">Durchschnittlich</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-500 rounded" />
              <span className="text-neutral-300">Ausreichend</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded" />
              <span className="text-neutral-300">Gering</span>
            </div>
          </div>
        </Card>
      ) : (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Resilience Trend</h3>
          <div className="text-center text-neutral-400 py-8">
            <p>Keine Resilience-Daten verfügbar</p>
            <p className="text-sm mt-2">Chart wird angezeigt, sobald Daten vorhanden sind</p>
          </div>
        </Card>
      )}

      {/* Stress-Insights */}
      <StressInsightsCard ratioRating={ratioRating} stress={stress} />
    </div>
  );
};

// Named export only - no default export
